import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useCreateUser, useUpdateUser, useUser } from "../../api/use-user";
import { LoadingPaper } from "../ui/loading-paper";
import { ErrorPaper } from "../ui/error-paper";
import { ToastContext } from "../../contexts/toast-context";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";

export const UserDetails = () => {
  const params = useParams();
  const { t } = useTranslation(["validation", "user", "common"]);
  const [user, setUser] = useState(null);
  const [{ data, error: loadError, loading: loadLoading }] = useUser(
    params.userId
  );
  useEffect(() => {
    setUser(data);
  }, [data]);
  const navigate = useNavigate();

  const isNew = params.userId === "new";

  const { showSuccess } = useContext(ToastContext);

  const schema = yup.object({
    name: yup
      .string()
      .max(120, t("maxLengthValidation", { field: t("name"), max: 120 }))
      .required(t("requiredValidation", { field: t("name") })),
    email: yup
      .string()
      .emailValidatorJs(t("emailValidation", { field: t("email") }))
      .required(t("requiredValidation", { field: t("email") })),
    role: yup.string().required(t("requiredValidation", { field: t("role") })),
    ...(isNew && {
      password: yup
        .string()
        .min(8, t("minLengthValidation", { field: t("password"), min: 7 }))
        .required(t("requiredValidation", { field: t("password") })),
    }),
    ...(!isNew && {
      remainingProjects: yup
        .number()
        .integer()
        .moreThan(-1, t("moreThanValidation", { field: t("remainingProjects"), min: -1 }))
        .required(t("requiredValidation", { field: t("remainingProjects") })),
    })
  });
  const dv = useMemo(
    () => ({
      name: user?.name || "",
      email: user?.email || "",
      role: user?.role || "user",
      ...(isNew && {
        password: "",
      }),
      ...(!isNew && {
        remainingProjects: user && user.remainingProjects !== undefined ? user.remainingProjects : "",
      })
    }),
    [user, isNew]
  );

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    setError,
  } = useForm({
    defaultValues: dv,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(dv);
  }, [reset, dv]);

  const [{ loading: updateLoading, error: updateError }, executeUpdate] =
    useUpdateUser(user?.id);

  const [
    { data: createdUser, loading: createLoading, error: createError },
    executeCreate,
  ] = useCreateUser();

  useEffect(() => {
    if (createdUser?.id != null) navigate(`/users/${createdUser.id}`);
  }, [createdUser, navigate]);

  const onSubmit = async (data) => {
    if (isNew) await executeCreate({ data });
    else {
      const res = await executeUpdate({ data });
      setUser(res.data);
      showSuccess(t("successfulAction"));
    }
  };
  const modifyError = updateError || createError;
  const errorMessage =
    modifyError &&
    (modifyError.response?.data?.error_message ||
      t("genericError", { action: t("updateProfile") }));

  useEffect(() => {
    const message = modifyError?.response?.data?.message || {};
    if (message[0] === "email is already taken")
      setError("email", { message: t("emailConflict") });
  }, [modifyError, setError, t]);

  if (loadLoading) return <LoadingPaper/>;

  if (loadError && params.userId !== "new")
    return (
      <ErrorPaper
        message={
          loadError.response.status === 404
            ? t("notFoundError", { object: t("user") })
            : t("genericError", { action: t("loadUser") })
        }
      />
    );
  return (
    <Card
      sx={{
        px: 5,
        py: 3,
        display: "flex",
        flexDirection: "column",
        width: "480px",
        maxWidth: "480px",
        overflow: "auto",
      }}
    >
      <Typography variant="h5" sx={{ flex: 1 }}>
        {!isNew && t("userDetailsTitle", { name: user?.name })}
        {isNew && t("newUser")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          mt: 4,
          "& > .MuiFormControl-root": {
            mb: 3,
          },
        }}
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          render={({ field }) => (
            <TextField
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message}
              label={t("name")}
              autoComplete="new-password"
              {...field}
            />
          )}
          control={control}
          name="name"
        />
        <Controller
          render={({ field }) => (
            <TextField
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              label={t("email")}
              autoComplete="new-password"
              {...field}
            />
          )}
          control={control}
          name="email"
        />
        {isNew && (
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                label={t("password")}
                autoComplete="new-password"
                type="password"
                {...field}
              />
            )}
          />
        )}
        <Controller
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="role-label">{t("role")}</InputLabel>
              <Select
                labelId="role-label"
                renderValue={(value) => t(value)}
                label={t("role")}
                {...field}
              >
                <MenuItem value={"user"}>{t("user")}</MenuItem>
                <MenuItem value={"developer"}>{t("developer")}</MenuItem>
                <MenuItem value={"admin"}>{t("admin")}</MenuItem>
              </Select>
              <FormHelperText className="Mui-error">{errors.role?.message}</FormHelperText>
            </FormControl>
          )}
          control={control}
          name="role"
        />
        {!isNew && (
          <Controller
            name="remainingProjects"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.remainingProjects}
                helperText={errors.remainingProjects?.message}
                label={t("remainingProjects")}
                autoComplete="new-password"
                {...field}
              />
            )}
          />
        )}
        {errorMessage && (
          <FormHelperText error>{errorMessage}</FormHelperText>
        )}
        <LoadingButton
          type="submit"
          loading={updateLoading || createLoading}
          disabled={!isDirty}
          variant="contained"
        >
          {t("save")}
        </LoadingButton>
      </Box>
    </Card>
  );
};
